// src/pages/AIMatchingPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/socketContext';
import { useAuth } from '../contexts/authContext';
import { API_URL } from '../lib/config';

import PartnerProfileCard from '../components/matching/PartnerProfileCard';
import './MatchingPage.css';

// ▼▼▼ 시간을 '오후 4:15'와 같은 형식으로 바꿔주는 이 함수를 수정합니다 ▼▼▼
const formatCooldownTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);

    // ★★★ 핵심: 여기에 1분을 추가합니다 ★★★
    date.setMinutes(date.getMinutes() + 1);

    return date.toLocaleTimeString('ko-KR', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    });
};

function AIMatchingPage() {
    const { currentUser } = useAuth();
    const [recommendation, setRecommendation] = useState(null);
    const [loading, setLoading] = useState(true);
    // 숫자(초) 대신, '종료 시간'을 문자열로 저장할 state
    const [cooldownEndTime, setCooldownEndTime] = useState(null);
    const navigate = useNavigate();
    const socket = useSocket();

    const fetchRecommendation = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        setRecommendation(null);
        try {
            const idToken = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/api/recommendations`, { headers: { 'Authorization': `Bearer ${idToken}` } });

            if (response.status === 429) {
                const retryAfterSeconds = parseInt(response.headers.get('Retry-After'), 10) || 60;
                const endTime = new Date(Date.now() + retryAfterSeconds * 1000);
                const endTimeString = endTime.toISOString();
                
                // localStorage와 state에 '종료 시간'을 저장
                localStorage.setItem('recommendationCooldownEnd', endTimeString);
                setCooldownEndTime(endTimeString);
                return;
            }
            if (!response.ok) throw new Error('추천 상대를 불러오는 데 실패했습니다.');

            // 성공 시 쿨타임 정보를 지웁니다.
            localStorage.removeItem('recommendationCooldownEnd');
            setCooldownEndTime(null);

            const data = await response.json();
            setRecommendation(data);
            
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    // 페이지 로딩 시 실행되는 유일한 초기화 useEffect
    useEffect(() => {
        const savedCooldownEnd = localStorage.getItem('recommendationCooldownEnd');
        if (savedCooldownEnd && new Date(savedCooldownEnd) > new Date()) {
            setCooldownEndTime(savedCooldownEnd);
            setLoading(false);
            return; // 쿨타임 중이면 API 호출 안 함
        }
        // 쿨타임이 없으면 API 호출
        fetchRecommendation();
    }, [fetchRecommendation]);

    // 소켓 '듣기' 전용 useEffect
    useEffect(() => {
        if (!socket) return;
        const handleRecommendChatCreated = (data) => {
            // alert(`${data.chatRoomId}, ${data.partner}`);
            navigate('/chat', { state: { chatRoomId: data.chatRoomId, partner: data.partner, from: '/inbox' } });
        };
        const handleErrorFeedback = ({ message }) => {
            alert(message); // 서버가 보낸 에러 메시지를 alert 창으로 띄웁니다.
        };
        socket.on('recommend_chat_created', handleRecommendChatCreated);
        socket.on('error_feedback', handleErrorFeedback);
        return () => {
            socket.off('recommend_chat_created', handleRecommendChatCreated);
            socket.off('error_feedback', handleErrorFeedback);
        };
    }, [socket, navigate]);
    
    const handleStartChat = (partner) => {
        if (!socket || !currentUser) return alert('서버에 연결되지 않았습니다.');
        const initialMessage = prompt(`'${partner.nickname}'님에게 보낼 메시지를 입력하세요.`);

        if (initialMessage && initialMessage.trim() !== '') {
            // 1. 백엔드에 채팅방 생성을 요청합니다.
            socket.emit('create_recommend_chat', {
                senderId: localStorage.getItem('currentUserId'),
                receiverId: partner.userId,
                initialMessage: initialMessage.trim()
            });

            // 2. 응답을 기다리지 않고, 즉시 쪽지함으로 이동합니다.
            // navigate('/inbox'); 
        } else {
            alert('메시지를 입력해야 대화를 시작할 수 있습니다.');
        }
    };

    const handleRefreshRecommendation = () => {
        // 버튼을 누르면 그냥 fetchRecommendation 함수만 호출
        fetchRecommendation();
    };

    if (loading) {
        return <div className="matching-page-container"><h1>AI가 추천할 상대를 찾는 중...</h1></div>;
    }

    // isCooldownActive 변수로 현재 쿨타임 상태를 명확히 확인
    const isCooldownActive = cooldownEndTime && new Date(cooldownEndTime) > new Date();

    return (
        <div className="matching-page-container">
            <div id="stars"></div>
            <div id="stars2"></div>
            <div id="stars3"></div>

            <h1>AI 추천 상대</h1>

            {recommendation ? (
                <p className="status-text">AI가 당신에게 어울리는 대화 상대를 추천했습니다.</p>
            ) : (
                <p className="status-text">{isCooldownActive ? "잠시 후 새로운 상대를 추천받을 수 있습니다." : "추천할 상대를 찾고 있습니다."}</p>
            )}

            <div className="recommendation-list">
                {recommendation && (
                    <PartnerProfileCard
                        profile={recommendation}
                        onAccept={() => handleStartChat(recommendation)}
                        onReject={() => navigate('/inbox')}
                    />
                )}
            </div>

            <div className="matching-actions">
                <button
                    className="cancel-matching-button"
                    onClick={handleRefreshRecommendation}
                    disabled={isCooldownActive || loading} // 쿨타임 중이거나 로딩 중일 때 비활성화
                >
                    {isCooldownActive
                        ? `${formatCooldownTime(cooldownEndTime)}에 다시 시도하세요`
                        : '다시시도'}
                </button>
            </div>
            
            <button className="back-button" onClick={() => navigate('/inbox')}>
                쪽지함으로 돌아가기
            </button>
        </div>
    );
}

export default AIMatchingPage;